import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { updateWorkspaceFolder } from '../../../api/workspaces/folders';
import { WorkspaceFolder } from '../../../api/workspaces/folders/types';
import { UpdateWorkspaceFolderParameters } from '../../../api/workspaces/folders/updateWorkspaceFolder';

function updateWorkspaceFolderById({
  workspaceFolderId,
  newWorkspaceFolder,
}: {
  workspaceFolderId: string;
  newWorkspaceFolder: Partial<WorkspaceFolder>;
}) {
  return (folders: WorkspaceFolder[]) => {
    const updateWorkspaceFolder = produce(
      (
        allWorkspaceFolders: WorkspaceFolder[],
        workspaceFolderId: string,
        newWorkspaceFolder: Partial<WorkspaceFolder>
      ) => {
        const index = allWorkspaceFolders.findIndex(
          (folder) => folder.id === workspaceFolderId
        );
        if (index != -1)
          allWorkspaceFolders[index] = {
            ...allWorkspaceFolders[index],
            ...newWorkspaceFolder,
          };
      }
    );
    return updateWorkspaceFolder(
      folders,
      workspaceFolderId,
      newWorkspaceFolder
    );
  };
}

export function useUpdateWorkspaceFolder() {
  const queryClient = useQueryClient();
  return useMutation<
    WorkspaceFolder,
    unknown,
    UpdateWorkspaceFolderParameters,
    { oldFolders: WorkspaceFolder[] }
  >({
    mutationFn: updateWorkspaceFolder,
    onMutate: ({ workspaceId, name, workspaceFolderId }) => {
      const workspaceFolders = queryClient.getQueryData<WorkspaceFolder[]>(
        workspaceKeys.folders({ workspaceId })
      );

      if (!workspaceFolders) return;

      queryClient.setQueryData<WorkspaceFolder[]>(
        workspaceKeys.folders({ workspaceId }),
        updateWorkspaceFolderById({
          workspaceFolderId: workspaceFolderId,
          newWorkspaceFolder: { name: name },
        })
      );

      return { oldFolders: workspaceFolders };
    },

    onSettled: (_data, _error, { workspaceId }) => {
      return queryClient.invalidateQueries(
        workspaceKeys.folders({ workspaceId })
      );
    },
    onError: (_error, { workspaceId }, context) => {
      if (!context) return;
      const { oldFolders } = context;

      queryClient.setQueryData<WorkspaceFolder[]>(
        workspaceKeys.folders({ workspaceId }),
        oldFolders
      );
    },
  });
}
